// ═══════════════════════════════════════════════════════════════════════════
// COLLAB-LOCK-COMMAND — pure WS frame builders/parsers (`/collab/ws`)
// ═══════════════════════════════════════════════════════════════════════════
//
// PURE frame builders (outbound) + parsers/validators (inbound) for the
// subset of the `/collab/ws` protocol the LOCK/UNLOCK command surface needs
// — the client-side analogue of `apps/node-companion/src/collabFrames.ts`,
// but for the OPPOSITE direction (this module builds client→server frames
// and parses server→client frames; the server file does the reverse). No
// `this`, no socket — every parser takes an `unknown` parsed JSON value and
// returns a well-formed typed frame or `null` (the client drops a `null`
// frame silently, mirroring the companion's fail-closed single-frame-drop
// posture — a malformed inbound frame never crashes a live tab).
//
// Only the frame types the lock feature actually consumes are modelled here:
// `hello` / `lock-request` / `lock-release` / `bye` (outbound) and `welcome`
// / `lock-granted` / `lock-denied` / `lock-update` / `error` / `ping`
// (inbound). Presence / edit-operation / arbitration frames are OUT OF
// SCOPE for this PR (no presence tracking, no op relay) — an unrecognised
// `type` is simply not matched by `parseCollabInboundFrame`'s dispatcher and
// the caller ignores it, exactly like an unknown frame on the wire.
//
// LEAF module: imports only the typed model (`collabLockTypes.ts`). No
// socket I/O, no node-companion, no editorStore.
// ═══════════════════════════════════════════════════════════════════════════

import {
  type LockDenyReason,
  type LockScope,
  type LockScopeKind,
  type LockState,
  type ParticipantIdentity,
} from "./collabLockTypes";

// ── Client → server (outbound) frames ──────────────────────────────────────

/** The collab pairing hello: token + the document room + the participant identity. */
export type CollabOutboundHelloFrame = {
  type: "hello";
  token: string;
  documentId: string;
  identity: ParticipantIdentity;
};

/** A lock proposal / release for one scope. */
export type CollabOutboundLockFrame = {
  type: "lock-request" | "lock-release";
  scope: LockScope;
};

/** The app-level heartbeat pong the active participant answers `ping` with. */
export type CollabOutboundPongFrame = { type: "pong"; seq: number };

/** An explicit graceful leave. */
export type CollabOutboundByeFrame = { type: "bye" };

export type CollabOutboundFrame =
  | CollabOutboundHelloFrame
  | CollabOutboundLockFrame
  | CollabOutboundPongFrame
  | CollabOutboundByeFrame;

/** Build the pairing hello frame (sent once, on socket `open`, before any other frame). */
export function buildHelloFrame(params: {
  token: string;
  documentId: string;
  identity: ParticipantIdentity;
}): CollabOutboundHelloFrame {
  return {
    type: "hello",
    token: params.token,
    documentId: params.documentId,
    identity: params.identity,
  };
}

/** Build a `lock-request` frame for one scope. */
export function buildLockRequestFrame(scope: LockScope): CollabOutboundLockFrame {
  return { type: "lock-request", scope };
}

/** Build a `lock-release` frame for one scope. */
export function buildLockReleaseFrame(scope: LockScope): CollabOutboundLockFrame {
  return { type: "lock-release", scope };
}

/** Build the explicit graceful-leave frame. */
export function buildByeFrame(): CollabOutboundByeFrame {
  return { type: "bye" };
}

/** Serialize an outbound frame for `socket.send`. Pure JSON — no transport concern. */
export function serializeCollabOutboundFrame(frame: CollabOutboundFrame): string {
  return JSON.stringify(frame);
}

// ── Server → client (inbound) frames ───────────────────────────────────────

/**
 * The `welcome` reply to a valid hello — server-minted `participantId` + a
 * bounded room snapshot (peers + active locks + the document revision).
 * `snapshot.peers` is NOT modelled beyond "an array" — presence tracking is
 * out of scope for this PR (no consumer reads it yet); `snapshot.locks` IS
 * fully typed since it seeds the client's live lock map.
 */
export type CollabInboundWelcomeFrame = {
  type: "welcome";
  participantId: string;
  documentId: string;
  bootId: string;
  documentRevision: number;
  snapshot: {
    peers: readonly unknown[];
    locks: readonly LockState[];
    truncated: boolean;
  };
};

/** A direct reply to MY OWN `lock-request`: the scope was granted. */
export type CollabInboundLockGrantedFrame = { type: "lock-granted"; lock: LockState };

/** A direct reply to MY OWN `lock-request`: the scope was denied. */
export type CollabInboundLockDeniedFrame = {
  type: "lock-denied";
  scope: LockScope;
  reason: LockDenyReason;
  heldBy: LockState | null;
};

/**
 * A BROADCAST lock delta (granted + released scopes) fired any time a lock
 * changes in the room — including a release triggered by ANOTHER
 * participant, or my own release settling. NOTE (server asymmetry, see
 * `collabServer.ts` `handleLockRequest`/`handleLockRelease`): a GRANT
 * broadcast excludes the granter's own socket (it already learned the
 * outcome via `lock-granted`), but a RELEASE broadcast is sent to
 * EVERY socket including the releaser — `collabLockClient.ts` relies on
 * this asymmetry to resolve `releaseLocks()` off this frame.
 */
export type CollabInboundLockUpdateFrame = {
  type: "lock-update";
  granted: readonly LockState[];
  released: readonly LockScope[];
};

/**
 * A generic server error notice. Only the `lock-refused` code (sent when the
 * room rejects a lock-request OUTSIDE the normal grant/deny outcome — e.g.
 * an unknown participant or a role gate at the room-membership layer,
 * `collabRoom.ts` `requesterFor`) is meaningful to the lock feature; other
 * codes (`op-refused`, `arbitration-refused`, …) belong to out-of-scope
 * subsystems and are parsed but otherwise ignored by the lock client.
 */
export type CollabInboundErrorFrame = {
  type: "error";
  code: string;
  reason?: string;
  scope?: LockScope;
  opId?: string;
};

/** The app-level heartbeat ping the client must answer with `{type:"pong",seq}`. */
export type CollabInboundPingFrame = { type: "ping"; seq: number };

export type CollabInboundFrame =
  | CollabInboundWelcomeFrame
  | CollabInboundLockGrantedFrame
  | CollabInboundLockDeniedFrame
  | CollabInboundLockUpdateFrame
  | CollabInboundErrorFrame
  | CollabInboundPingFrame;

// ── Parse helpers (validate types, never trust `unknown` blindly) ─────────

function asRecord(value: unknown): Record<string, unknown> | null {
  return typeof value === "object" && value !== null ? (value as Record<string, unknown>) : null;
}

function nonEmptyString(value: unknown): string | null {
  return typeof value === "string" && value.length > 0 ? value : null;
}

function finiteNumber(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

const VALID_LOCK_SCOPE_KINDS: ReadonlySet<LockScopeKind> = new Set<LockScopeKind>([
  "entity",
  "layer",
]);

const VALID_LOCK_DENY_REASONS: ReadonlySet<LockDenyReason> = new Set<LockDenyReason>([
  "held-by-other",
  "role-cannot-lock",
  "lock-limit",
]);

/** Parse a `LockScope` (`{kind, targetId}`), else `null`. */
export function parseLockScope(value: unknown): LockScope | null {
  const rec = asRecord(value);
  if (rec === null) return null;
  if (typeof rec.kind !== "string" || !VALID_LOCK_SCOPE_KINDS.has(rec.kind as LockScopeKind)) {
    return null;
  }
  const targetId = nonEmptyString(rec.targetId);
  if (targetId === null) return null;
  return { kind: rec.kind as LockScopeKind, targetId };
}

/** Parse a `LockState` (`{scope, ownerId, ownerUserId, ownerName, grantedAtMs, expiresAtMs}`), else `null`. */
export function parseLockState(value: unknown): LockState | null {
  const rec = asRecord(value);
  if (rec === null) return null;
  const scope = parseLockScope(rec.scope);
  const ownerId = nonEmptyString(rec.ownerId);
  const ownerUserId = nonEmptyString(rec.ownerUserId);
  const ownerName = nonEmptyString(rec.ownerName);
  const grantedAtMs = finiteNumber(rec.grantedAtMs);
  const expiresAtMs = finiteNumber(rec.expiresAtMs);
  if (
    scope === null ||
    ownerId === null ||
    ownerUserId === null ||
    ownerName === null ||
    grantedAtMs === null ||
    expiresAtMs === null
  ) {
    return null;
  }
  return { scope, ownerId, ownerUserId, ownerName, grantedAtMs, expiresAtMs };
}

function parseLockStateArray(value: unknown): readonly LockState[] | null {
  if (!Array.isArray(value)) return null;
  const out: LockState[] = [];
  for (const element of value) {
    const lock = parseLockState(element);
    if (lock === null) return null;
    out.push(lock);
  }
  return out;
}

function parseLockScopeArray(value: unknown): readonly LockScope[] | null {
  if (!Array.isArray(value)) return null;
  const out: LockScope[] = [];
  for (const element of value) {
    const scope = parseLockScope(element);
    if (scope === null) return null;
    out.push(scope);
  }
  return out;
}

/** Narrow an unknown parsed frame to a well-formed `welcome`, else `null`. */
export function parseWelcomeFrame(parsed: unknown): CollabInboundWelcomeFrame | null {
  const rec = asRecord(parsed);
  if (rec === null || rec.type !== "welcome") return null;
  const participantId = nonEmptyString(rec.participantId);
  const documentId = nonEmptyString(rec.documentId);
  const bootId = nonEmptyString(rec.bootId);
  const documentRevision = finiteNumber(rec.documentRevision);
  const snapshotRec = asRecord(rec.snapshot);
  if (
    participantId === null ||
    documentId === null ||
    bootId === null ||
    documentRevision === null ||
    snapshotRec === null
  ) {
    return null;
  }
  if (!Array.isArray(snapshotRec.peers)) return null;
  const locks = parseLockStateArray(snapshotRec.locks);
  if (locks === null || typeof snapshotRec.truncated !== "boolean") return null;
  return {
    type: "welcome",
    participantId,
    documentId,
    bootId,
    documentRevision,
    snapshot: { peers: snapshotRec.peers, locks, truncated: snapshotRec.truncated },
  };
}

/** Narrow an unknown parsed frame to a well-formed `lock-granted`, else `null`. */
export function parseLockGrantedFrame(parsed: unknown): CollabInboundLockGrantedFrame | null {
  const rec = asRecord(parsed);
  if (rec === null || rec.type !== "lock-granted") return null;
  const lock = parseLockState(rec.lock);
  if (lock === null) return null;
  return { type: "lock-granted", lock };
}

/** Narrow an unknown parsed frame to a well-formed `lock-denied`, else `null`. */
export function parseLockDeniedFrame(parsed: unknown): CollabInboundLockDeniedFrame | null {
  const rec = asRecord(parsed);
  if (rec === null || rec.type !== "lock-denied") return null;
  const scope = parseLockScope(rec.scope);
  if (
    scope === null ||
    typeof rec.reason !== "string" ||
    !VALID_LOCK_DENY_REASONS.has(rec.reason as LockDenyReason)
  ) {
    return null;
  }
  let heldBy: LockState | null = null;
  if (rec.heldBy !== null && rec.heldBy !== undefined) {
    heldBy = parseLockState(rec.heldBy);
    if (heldBy === null) return null;
  }
  return { type: "lock-denied", scope, reason: rec.reason as LockDenyReason, heldBy };
}

/** Narrow an unknown parsed frame to a well-formed `lock-update`, else `null`. */
export function parseLockUpdateFrame(parsed: unknown): CollabInboundLockUpdateFrame | null {
  const rec = asRecord(parsed);
  if (rec === null || rec.type !== "lock-update") return null;
  const granted = parseLockStateArray(rec.granted);
  const released = parseLockScopeArray(rec.released);
  if (granted === null || released === null) return null;
  return { type: "lock-update", granted, released };
}

/** Narrow an unknown parsed frame to a well-formed `error`, else `null`. */
export function parseErrorFrame(parsed: unknown): CollabInboundErrorFrame | null {
  const rec = asRecord(parsed);
  if (rec === null || rec.type !== "error") return null;
  const code = nonEmptyString(rec.code);
  if (code === null) return null;
  const frame: CollabInboundErrorFrame = { type: "error", code };
  if (typeof rec.reason === "string") frame.reason = rec.reason;
  if (rec.scope !== undefined) {
    const scope = parseLockScope(rec.scope);
    if (scope !== null) frame.scope = scope;
  }
  if (typeof rec.opId === "string") frame.opId = rec.opId;
  return frame;
}

/** Narrow an unknown parsed frame to a well-formed `ping`, else `null`. */
export function parsePingFrame(parsed: unknown): CollabInboundPingFrame | null {
  const rec = asRecord(parsed);
  if (rec === null || rec.type !== "ping") return null;
  const seq = finiteNumber(rec.seq);
  if (seq === null) return null;
  return { type: "ping", seq };
}

/**
 * Dispatch a raw parsed JSON value to its specific inbound parser by `type`.
 * Returns the typed frame, or `null` for a frame type this lock client does
 * not model (presence / op / arbitration — out of scope for this PR) or a
 * malformed body of a modelled type. The caller drops a `null` — a single
 * bad/irrelevant frame never disconnects a live tab.
 */
export function parseCollabInboundFrame(parsed: unknown): CollabInboundFrame | null {
  const rec = asRecord(parsed);
  if (rec === null || typeof rec.type !== "string") return null;
  switch (rec.type) {
    case "welcome":
      return parseWelcomeFrame(parsed);
    case "lock-granted":
      return parseLockGrantedFrame(parsed);
    case "lock-denied":
      return parseLockDeniedFrame(parsed);
    case "lock-update":
      return parseLockUpdateFrame(parsed);
    case "error":
      return parseErrorFrame(parsed);
    case "ping":
      return parsePingFrame(parsed);
    default:
      return null;
  }
}
